'use client'

import clsx from 'clsx'
import { ReactNode, useMemo, useState } from 'react'

import { HelpCenterButton } from '../../HelpCenter/HelpCenter'
import Icon from '../../Icon/Icon'

import { ModalButton } from 'lib/components/Modal/Modal'

export interface InputAddonProps {
  /**
   * [InputAddon] Opens up the HelpCenter dialog, expanding the item ID provided.
   * - `help.id` : The HelpCenter's item ID to expand
   * - `help.content` : The text to show beside the help icon
   */
  help?: {
    id?: string
    content?: string | ReactNode
    className?: string
  }
  /**
   * [InputAddon] Disables the input, and shows an "Edit" button to enable it back.
   */
  conditionalEdit?: boolean
  /**
   * [InputAddon] Callback that runs when the "Edit" button is clicked.
   */
  onEdit?: (isEnabled: true) => void
  /**
   * [InputAddon] Shows up a link on an input.
   */
  link?: {
    url: string
    content: string | ReactNode
    className?: string
  }

  /**
   * [InputAddon] Shows a modal when clicked.
   */
  modal?: {
    modalId: string
    content: string | ReactNode
    className?: string
    source?: string
  }
}

export function InputAddonHelp(props: InputAddonProps['help']) {
  return (
    <HelpCenterButton
      item={props?.id}
      render={(buttonProps) => (
        <button
          type="button"
          {...buttonProps}
          className={clsx(
            'inline-flex items-center type-body-10 text-neutral-700 whitespace-nowrap',
            props?.className,
          )}
        >
          {props?.content}&nbsp;
          <Icon icon="help-circle-outline" className="w-[1.3em] h-[1.3em]" />
        </button>
      )}
    />
  )
}

export function InputAddonLink(props: InputAddonProps['link']) {
  return (
    <a
      href={props?.url || ''}
      className={clsx(
        'type-body-9 text-neutral-700 underline underline-offset-[4px] whitespace-nowrap',
        props?.className,
      )}
    >
      {props?.content}
    </a>
  )
}

export function InputAddonModalButton(
  props: InputAddonProps['modal'] = {
    modalId: '',
    content: '',
    className: '',
    source: '',
  },
) {
  const { modalId, content, className, source } = props

  return (
    <ModalButton
      targetModal={modalId}
      args={{ source }}
      render={(modalButtonProps) => (
        <button
          type="button"
          className={clsx(
            'type-body-9 text-neutral-700 underline underline-offset-[4px] whitespace-nowrap',
            className,
          )}
          onClick={() => {
            //handleModalOnClick()
            modalButtonProps.onClick?.()
          }}
        >
          {content}
        </button>
      )}
    />
  )
}

export function InputAddonConditionalEdit(props: InputAddonProps) {
  const [inputDisabled, setInputDisabled] = useState(
    props?.conditionalEdit || false,
  )

  const onClick = () => {
    props.onEdit?.(true)
    setInputDisabled(false)
  }

  return (
    inputDisabled && (
      <button
        type="button"
        className="type-body-9 underline underline-offset-[4px] cursor-pointer"
        onClick={onClick}
      >
        {' '}
        Edit
      </button>
    )
  )
}

export default function InputAddon(props: InputAddonProps) {
  const isHelp = 'help' in props
  const isLink = 'link' in props
  const isConditionalEdit = 'conditionalEdit' in props
  const isModal = 'modal' in props

  // Ensure that only one addon functionality is provided per instance
  if ([isHelp, isLink, isConditionalEdit, isModal].filter(Boolean).length > 1) {
    throw new Error(
      'InputAddon can only accept one of three variations: "help" | "link" | "conditionalEdit" | "modal"',
    )
  }

  const RenderedElement = useMemo(() => {
    if (isHelp) {
      return (
        <InputAddonHelp
          id={props.help?.id}
          content={props.help?.content}
          className={props.help?.className}
        />
      )
    }
    if (isLink) {
      return (
        <InputAddonLink
          url={props.link?.url || ''}
          content={props.link?.content}
          className={props.link?.className}
        />
      )
    }
    if (isConditionalEdit) {
      return (
        props.conditionalEdit && (
          <InputAddonConditionalEdit
            conditionalEdit={props.conditionalEdit}
            onEdit={props.onEdit}
          />
        )
      )
    }

    if (isModal) {
      return (
        props.modal && (
          <InputAddonModalButton
            modalId={props.modal.modalId}
            content={props.modal.content}
            className={props.modal.className}
            source={props.modal.source}
          />
        )
      )
    }
  }, [
    isHelp,
    isLink,
    isConditionalEdit,
    isModal,
    props.help?.id,
    props.help?.content,
    props.help?.className,
    props.link?.url,
    props.link?.content,
    props.link?.className,
    props.conditionalEdit,
    props.onEdit,
    props.modal,
  ])

  return RenderedElement
}
